import type { ReactNode } from 'react';

import { useParams } from '@suntex.core/nav';
import type { EntityLevel } from '@suntex.fim/enums';

import { parseEntityId } from '../utils/parseEntityId';

import type { CommonLayoutProps } from './CommonLayout';
import { EntityLayoutShell } from './EntityLayoutShell';

/** Chrome props shared by every entity-scoped layout wrapper. */
export type EntityLayoutProps = Pick<CommonLayoutProps, 'appVersion'>;

interface EntityLayoutConfig<TId extends number = number> {
  entityLevel: EntityLevel;
  /** Route param name, also the entity slug (e.g. `teamId`). */
  paramName: 'teamId' | 'schoolId' | 'districtId' | 'regionId';
  /** Optional domain provider fed the validated id (e.g. `TeamProvider`). */
  renderProvider?: (id: TId | undefined, children: ReactNode) => ReactNode;
}

/**
 * Build a layout wrapper for an entity-scoped route: it reads and validates the
 * id param, exposes it via {@link EntityProvider}, optionally wraps a domain
 * provider, and renders the shared {@link CommonLayout} chrome.
 */
export function createEntityLayout<TId extends number = number>({
  entityLevel,
  paramName,
  renderProvider,
}: EntityLayoutConfig<TId>) {
  function EntityLayout(props: Readonly<EntityLayoutProps>) {
    const params = useParams();
    const id = parseEntityId<TId>(params[paramName]);
    return (
      <EntityLayoutShell
        {...props}
        entityLevel={entityLevel}
        entityId={id}
        wrap={renderProvider && ((layout) => renderProvider(id, layout))}
      />
    );
  }
  EntityLayout.displayName = `EntityLayout(${paramName})`;
  return EntityLayout;
}
